import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { BUSINESS } from "@/components/site/constants";

export const Route = createFileRoute("/storm-damage-roof-repair")({
  head: () => ({
    meta: [
      { title: "Storm Damage Roof Repair in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Get roof storm-damage inspection and repair in Citrus County for wind, hail, missing shingles, leaks and fallen-debris damage." },
      { property: "og:title", content: "Storm Damage Roof Repair in Citrus County, FL" },
      { property: "og:description", content: "Post-storm roof inspection and repair for wind, hail and debris damage." },
      { property: "og:url", content: canonical("/storm-damage-roof-repair") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/storm-damage-roof-repair") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Storm Damage Roof Repair", href: "/storm-damage-roof-repair" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Storm Damage Roof Repair",
          serviceType: "Storm Damage Roof Repair",
          description: "Post-storm roof inspection, temporary dry-in, documentation and repair for wind, hail and debris damage in Citrus County, FL.",
          url: canonical("/storm-damage-roof-repair"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Storm Damage Roof Repair", href: "/storm-damage-roof-repair" }]} />
      <PageHero showRating
        eyebrow="Storm Damage"
        title="Storm Damage Roof Repair in Citrus County, FL"
        subtitle={BUSINESS.emergencyLine}
      />
      <ServicePageBody title="Storm Damage Roof Repair" service="Storm Damage Roof Repair">
        <h2>What we look for after a Florida storm</h2>
        <ul>
          <li>Missing, lifted or creased shingles</li>
          <li>Exposed nails and torn underlayment</li>
          <li>Hail bruising or granule loss</li>
          <li>Debris impact on shingles, metal panels or flashings</li>
          <li>Ceiling stains and active water intrusion</li>
          <li>Damaged gutters, soffit or fascia that let water back up under the roof</li>
        </ul>

        <h2>Immediate homeowner safety steps</h2>
        <ul>
          <li>Do not walk on a damaged roof yourself.</li>
          <li>If water is coming in, move belongings and contain drips with buckets and towels.</li>
          <li>Photograph any exterior damage from ground level.</li>
          <li>Contact a licensed roofer for an on-roof inspection.</li>
        </ul>

        <h2>Temporary dry-in and tarping</h2>
        <p>
          When a roof cannot be permanently repaired the same day, we can install a temporary tarp or dry-in to keep water out until the permanent repair or replacement is completed.
        </p>

        <h2>Documentation and estimates</h2>
        <p>
          We photograph what we find, provide a written estimate for the recommended repair or replacement, and give you what you need to submit to your insurance company. Wind and hail damage may be covered by homeowners insurance, but coverage depends on the cause of loss, the policy language and the applicable deductible. Ultimately, <strong>the insurance company determines whether the claim is covered</strong>.
        </p>

        <h2>After-hours emergency response</h2>
        <p>
          {BUSINESS.emergencyLine} We do not guarantee a specific arrival time, but we will do everything we can to help a Citrus County homeowner get their roof stabilized.
        </p>

        <h2>Related</h2>
        <p>
          See <Link to="/roof-repair">roof repair</Link>, <Link to="/roof-replacement">roof replacement</Link>, <Link to="/roof-inspection">free roof inspection</Link>, learn how <Link to="/florida-roof-insurance-rules">Florida&apos;s roof insurance rules work</Link>, and read our article on <Link to="/blog/signs-roof-needs-repair-citrus-county">signs your Citrus County roof may need repair after a storm</Link>.
        </p>
      </ServicePageBody>
    </>
  );
}
